import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts'
import { Box, Stack } from '@mui/material'
import moment from 'moment'

import EmptyScreen from 'packages/ui/EmptyScreen'
import ChartSkeleton from 'components/global/CUK/Skeletons/ChartSkeleton'

import EvolutionTooltip from './EvolutionTooltip'
import useGraph from './use-graph'

const EvolutionActiveUsers = () => {
  const { t } = useTranslation()
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const {loading, data} = useGraph()

  const evolutionData = useMemo(() => {
    return data.map(item => ({
      date: item.date,
      monthName: moment(item.date).format('MMM'),
      count_past_year: item.active_users_past_year || null,
      count_current_year: item.active_users_current_year || null,
      percentage: item.percentage_active_users.toFixed(0),
      hasCurrentData: item.active_users_current_year > 0,
      hasPastData: item.active_users_past_year > 0
    }))
  }, [data])

  if (loading) {
    return <ChartSkeleton />
  }

  if (!data.length) {
    return (
      <Box sx={{ 
        width: '100%',
        height: '100%',
        border: '1px dashed #D7DAEA',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px'
      }}>
        <EmptyScreen 
          icon="square-poll-vertical" 
          variant="compact"
          iconSize="32px" 
          fontSize="14px"
          spacing={1.5}
        >
          {t('stats.evolution.no_data')}
        </EmptyScreen>
      </Box>
    )
  }

  const getLineOpacity = () => {
    if (hoveredIndex === null) return 1
    return 0.3
  }

  return (
    <Stack height={'100%'}>
    <ResponsiveContainer>
      <LineChart
        width={500}
        height={300}
        data={evolutionData}
        margin={{
          top: 40,
          right: 5,
          left: 20,
          bottom: 5,
        }}
        onMouseMove={(data) => {
          if (data && data.activeTooltipIndex !== undefined) {
            setHoveredIndex(data.activeTooltipIndex)
          }
        }}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <XAxis 
          dataKey="monthName" 
          style={{ fontSize: '0.75rem', fontWeight: 400 }}
        />
        <YAxis 
          style={{ fontSize: '0.75rem', fontWeight: 400 }}
        />
        <Tooltip
          cursor={false}
          wrapperStyle={{ outline: 'none', zIndex: 9999 }}
          content={<EvolutionTooltip isActiveUsers={true} />}
          position={{ y: -130 }}
        />
        <Line
          dataKey="count_past_year"
          connectNulls={false}
          stroke="#A5D9CB"
          fill="#A5D9CB"
          strokeWidth={2}
          dot={false}
          strokeOpacity={getLineOpacity()}
          name={(new Date().getFullYear() - 1).toString()}
        />
        <Line
          dataKey="count_current_year"
          connectNulls={false}
          stroke="#4F8AA4"
          fill="#4F8AA4"
          strokeWidth={2}
          dot={false}
          strokeOpacity={getLineOpacity()}
          name={new Date().getFullYear().toString()}
        />
      </LineChart>
    </ResponsiveContainer>
    </Stack>
  )
}

export default EvolutionActiveUsers